import React from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { loadPlayerSDK } from 'utils/loadPlayerSDK';
import { currentUserSelector } from 'common/selectors/currentUser';
import { getPlayerAnalyticsConsentSelector } from 'common/selectors';
import { getToken, refreshToken } from 'utils/keycloak';
import { CtPrivacyRepository } from 'common/api/models';
import { ProductName } from '../mi-player/map-to-youbora-config';

class InternalVideoPlayerEmbed extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      playerIsReady: false,
      playerState: 'pause',
      playerCurrentTime: 0,
    };
    this.isUnmounted = false;
    this.pollingPlayerSDK = null;
    this.playerContainerId = props.id;
  }

  componentDidMount() {
    this.addPlayer();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userGaveConsent !== this.props.userGaveConsent) {
      const players = window.VideoPlayer.Collection;

      if (players) {
        this.acceptOrRejectAnalytics(players);
      }
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    clearInterval(this.pollingPlayerSDK);

    const players = window?.VideoPlayer?.Collection;

    if (players) {
      try {
        players.removePlayerById(this.playerContainerId);
      } catch (e) {
        // in case it can not be removed just ignore it
      }
    }
  }

  async updatePlayerHeader() {
    if (CtPrivacyRepository.isPrivate()) {
      await refreshToken();
      window.VideoPlayer.HttpCallHelper.addHeader('Authorization', `Bearer ${getToken()}`);
    }
  }

  async addPlayer() {
    const watermark = this.props.watermark ? { content: this.props.watermark } : undefined;

    if (window.VideoPlayer) {
      // the embed tag is only parsed by the player one time (document-ready)
      // to create a new instance we have to use the api now.
      // The parameters on the embed tag will be readed by the player.
      // After UIT has updated player version from 2.4 to a newer version. (at least version 2.6.9)
      // this can be replaced by always initializing it over the api
      // see git history and the link to the player doc.
      const players = window.VideoPlayer.Collection;

      await this.updatePlayerHeader();
      this.acceptOrRejectAnalytics(players);

      players.addPlayerById(this.playerContainerId, {
        success: (player) => this.onPlayerReady(player),
        watermark,
      });
    } else {
      const { playerConfiguration } = this.props;
      const pollSDK = (maxTry) => {
        return new Promise((resolve, reject) => {
          let count = 0;

          this.pollingPlayerSDK = setInterval(() => {
            if (window.VideoPlayer?.Collection) {
              clearInterval(this.pollingPlayerSDK);
              resolve();
            }
            if (count === maxTry) {
              clearInterval(this.pollingPlayerSDK);
              reject(new Error('Player SDK not available after 60 seconds'));
            }

            count++;
          }, 100);
        });
      };

      const callback = async () => {
        if (this.isUnmounted) {
          return;
        }

        if (!window.VideoPlayer?.Collection) {
          // after loading the player sdk we have to wait until the player is available
          await pollSDK(600).catch((err) => {
            console.error('Polling Error: ', err);
            return;
          });
        }

        const players = window.VideoPlayer.Collection;
        await this.updatePlayerHeader();
        this.acceptOrRejectAnalytics(players);

        players.addPlayerById(this.playerContainerId, {
          success: (player) => this.onPlayerReady(player),
          watermark,
        });
      };

      loadPlayerSDK(playerConfiguration).then(callback.bind(this));
    }
  }

  acceptOrRejectAnalytics(players) {
    if (this.props.userGaveConsent === undefined) {
      return;
    }

    if (this.props.userGaveConsent) {
      players.acceptAnalytics();
    } else {
      players.rejectAnalytics();
    }
  }

  onPlayerReady(player) {
    if (this.isUnmounted) {
      return;
    }

    player.registerEventListener('pause', () => {
      this.setState({ playerState: 'pause' });
    });

    player.registerEventListener('play', () => {
      this.setState({ playerState: 'play' });
    });

    player.registerEventListener('timeupdate', () => {
      this.setState({ playerCurrentTime: player.getCurrentTime() });
    });

    // playing event will be fired instead of play
    // in vmpro player version 2.4 (UIT) instead of "play"
    // thats not the case in newer versions
    player.registerEventListener('playing', () => {
      this.setState({ playerState: 'play' });
    });

    this.setState({ playerIsReady: true });
  }

  getChannelId() {
    const urlElements = window.location.pathname.split('/');
    return urlElements[urlElements.indexOf('channel') + 1] || this.props.video.channels[0]?.id;
  }

  render() {
    const { video, playerConfiguration } = this.props;
    const videoBar = this.state.playerIsReady ? '' : ' ct-video-player-embed__video-bar--hidden';

    return (
      <div className={`ct-video-player-embed${videoBar}`} data-e2e="ct-embeded-player">
        <div
          id={this.playerContainerId}
          data-e2e-player-state={this.state.playerState}
          data-e2e-player-is-ready={this.state.playerIsReady}
          data-e2e-player-current-time={Math.floor(this.state.playerCurrentTime)}
          mi24-video-player=""
          disable-auto-creation="true"
          video-id={video.id}
          channel-id={this.getChannelId()}
          embed-product-name={ProductName}
          player-id={playerConfiguration.playerDefinitionId}
          token={video.playerToken}
          api-url={`https://${playerConfiguration.playerApiDomain}/play`}
          disable-tracking-dialogue="true"
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  watermark: currentUserSelector(state).watermark,
  userGaveConsent: getPlayerAnalyticsConsentSelector(state),
});

export const VideoPlayerEmbed = connect(mapStateToProps)(InternalVideoPlayerEmbed);

VideoPlayerEmbed.propTypes = {
  playerConfiguration: PropTypes.object.isRequired,
  video: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
};
